import React, { createContext, useContext, useState } from "react";
import { externalOnlySchema } from "@features/vessel-call/vessel-call-status/PortServices/flow/schemas/approvedByMDGarbageDisposalSchema";
import type {
  MarpolAnnexI,
  Resource,
} from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import type { PortServiceResourceEquipment } from "@services/port-services/types";

export type PortServicesFlowErrors = {
  internalNotes?: string;
  externalNotes?: string;
  rejectionReason?: string;
  actualNumberOfSkips?: string;
  actualQuantity?: string;
  specialInstructions?: string;
  notes?: string;
  chargedParty?: string;
  numberOfPackagesHandled?: string;
  spares?: string;
  incidents?: string;
  resourcesAndEquipment?: string;
  garbageDisposalDocument?: string;
  billOfLadingAttachment?: string;
  billOfLading?: string;
  cargoType?: string;
  operationType?: string;
  serviceReportAttachment?: string;
  images?: string;
  slopsDisposalDocuments?: string;
  ats?: string | Date | null;
  atc?: string | Date | null;
  etc?: string | Date | null;
  ets?: string | Date | null;
  notificationOption?: string;
  rejectionFile?: string;
  internalFile?: string;
  externalFile?: string;
  files?: string;
  attachment?: string;
  wasteResiduesForDischargeFacility?: MarpolAnnexI[];
  resources?: string;
};

interface PortServicesFlowContextValue {
  internalNotes: string;
  setInternalNotes: (notes: string) => void;
  internalAttachment: { documentUrl: string; documentName: string; fileType: string };
  setInternalAttachment: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  externalNotes: string;
  setExternalNotes: (notes: string) => void;
  externalAttachment: { documentUrl: string; documentName: string; fileType: string };
  setExternalAttachment: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  externalOnlySchema: typeof externalOnlySchema;
  rejectionReason: string;
  setRejectionReason: (reason: string) => void;
  rejectionAttachment: { documentUrl: string; documentName: string; fileType: string };
  setRejectionAttachment: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  actualNumberOfSkips?: number;
  setActualNumberOfSkips: (num: number | undefined) => void;
  wasteResiduesForDischargeFacility?: MarpolAnnexI[];
  setWasteResiduesForDischargeFacility: (wastes: MarpolAnnexI[] | undefined) => void;
  actualQuantity?: number;
  setActualQuantity: (quantity: number | undefined) => void;
  specialInstructions: string;
  setSpecialInstructions: (specialInstructions: string) => void;
  notes: string;
  setNotes: (notes: string) => void;
  chargedParty: string;
  setChargedParty: (chargedParty: string) => void;
  resourcesAndEquipment: PortServiceResourceEquipment[];
  setResourcesAndEquipment: (resources: PortServiceResourceEquipment[]) => void;
  garbageDisposalDocument: { documentUrl: string; documentName: string; fileType: string };
  setGarbageDisposalDocument: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  billOfLading: { documentUrl: string; documentName: string; fileType: string };
  setBillOfLading: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  cargoType: string;
  setCargoType: (cargoType: string) => void;
  operationType: string;
  setOperationType: (operationType: string) => void;
  numberOfPackagesHandled: number | undefined;
  setNumberOfPackagesHandled: (numberOfPackagesHandled: number | undefined) => void;
  spares: number | undefined;
  setSpares: (spares: number | undefined) => void;
  incidents: string;
  setIncidents: (incidents: string) => void;
  resources?: Resource[];
  setResources: (resources: Resource[] | undefined) => void;
  serviceReportAttachment: { documentUrl: string; documentName: string; fileType: string };
  setServiceReportAttachment: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  attachment: { documentUrl: string; documentName: string; fileType: string };
  setAttachment: (attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  }) => void;
  images: { documentUrl: string; documentName: string; fileType: string }[];
  setImages: (
    attachment: {
      documentUrl: string;
      documentName: string;
      fileType: string;
    }[],
  ) => void;
  slopsDisposalDocuments: { documentUrl: string; documentName: string; fileType: string }[];
  setSlopsDisposalDocuments: (
    documents: { documentUrl: string; documentName: string; fileType: string }[],
  ) => void;
  ats: string | Date | null;
  setAts: (arg: Date | null) => void;
  atc: string | Date | null;
  setAtc: (atc: string | Date | null) => void;
  etc: string | Date | null;
  setEtc: (etc: string | Date | null) => void;
  ets: string | Date | null;
  setEts: (ets: string | Date | null) => void;
  errors: PortServicesFlowErrors;
  setErrors: React.Dispatch<React.SetStateAction<PortServicesFlowErrors>>;
}

export const PortServicesFlowContext = createContext<PortServicesFlowContextValue | null>(null);

export const PortServicesFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [internalNotes, setInternalNotes] = useState("");
  const [internalAttachment, setInternalAttachment] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [externalNotes, setExternalNotes] = useState("");
  const [externalAttachment, setExternalAttachment] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionAttachment, setRejectionAttachment] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [actualNumberOfSkips, setActualNumberOfSkips] = useState<number | undefined>(undefined);
  const [wasteResiduesForDischargeFacility, setWasteResiduesForDischargeFacility] = useState<
    MarpolAnnexI[] | undefined
  >(undefined);
  const [actualQuantity, setActualQuantity] = useState<number | undefined>(undefined);
  const [numberOfPackagesHandled, setNumberOfPackagesHandled] = useState<number | undefined>(
    undefined,
  );
  const [spares, setSpares] = useState<number | undefined>(undefined);
  const [incidents, setIncidents] = useState<string>("");
  const [resources, setResources] = useState<Resource[] | undefined>(undefined);
  const [specialInstructions, setSpecialInstructions] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [chargedParty, setChargedParty] = useState<string>("");
  const [resourcesAndEquipment, setResourcesAndEquipment] = useState<
    PortServiceResourceEquipment[]
  >([]);
  const [garbageDisposalDocument, setGarbageDisposalDocument] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [billOfLading, setBillOfLading] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [cargoType, setCargoType] = useState<string>("");
  const [operationType, setOperationType] = useState<string>("");
  const [serviceReportAttachment, setServiceReportAttachment] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [attachment, setAttachment] = useState({
    documentUrl: "",
    documentName: "",
    fileType: "",
  });
  const [images, setImages] = useState<
    {
      documentUrl: string;
      documentName: string;
      fileType: string;
    }[]
  >([]);
  const [slopsDisposalDocuments, setSlopsDisposalDocuments] = useState<
    { documentUrl: string; documentName: string; fileType: string }[]
  >([]);

  const [ats, setAts] = useState<string | Date | null>(null);
  const [atc, setAtc] = useState<string | Date | null>(null);
  const [etc, setEtc] = useState<string | Date | null>(null);
  const [ets, setEts] = useState<string | Date | null>(null);

  const [errors, setErrors] = useState<PortServicesFlowErrors>({});

  return (
    <PortServicesFlowContext.Provider
      value={{
        setInternalNotes,
        internalNotes,
        internalAttachment,
        setInternalAttachment,
        setExternalNotes,
        externalNotes,
        externalAttachment,
        setExternalAttachment,
        externalOnlySchema,
        rejectionReason,
        setRejectionReason,
        rejectionAttachment,
        setRejectionAttachment,
        setActualNumberOfSkips,
        actualNumberOfSkips,
        setWasteResiduesForDischargeFacility,
        wasteResiduesForDischargeFacility,
        setActualQuantity,
        actualQuantity,
        setSpecialInstructions,
        specialInstructions,
        setNotes,
        notes,
        chargedParty,
        setChargedParty,
        resourcesAndEquipment,
        setResourcesAndEquipment,
        setGarbageDisposalDocument,
        garbageDisposalDocument,
        setBillOfLading,
        billOfLading,
        setCargoType,
        cargoType,
        setOperationType,
        operationType,
        numberOfPackagesHandled,
        setNumberOfPackagesHandled,
        spares,
        setSpares,
        incidents,
        setIncidents,
        resources,
        setResources,
        setServiceReportAttachment,
        serviceReportAttachment,
        attachment,
        setAttachment,
        images,
        setImages,
        setSlopsDisposalDocuments,
        slopsDisposalDocuments,
        setAts,
        ats,
        setAtc,
        atc,
        setEtc,
        etc,
        setEts,
        ets,
        setErrors,
        errors,
      }}
    >
      {children}
    </PortServicesFlowContext.Provider>
  );
};

export function usePortServicesFlow(): PortServicesFlowContextValue {
  const ctx = useContext(PortServicesFlowContext);
  if (!ctx) throw new Error("usePortServicesFlow must be inside PortServicesFlowProvider");
  return ctx;
}
